import type {AddFormType} from 'context/guest';
import {FormField, GUEST_FORM_NAMES} from '@chekin/core';
import i18n from 'i18next';
import {FormFieldsMapper} from '../types';
import {FIELD_NAME_SUBGROUP, FIELD_SUBGROUP} from './fieldSubgroups';

type FieldGroup = {
  name: string;
  title: string;
  fields: FormField[];
  isCustom: boolean;
  order: number;
};

const getFieldSubgroupName = (field: FormField): string =>
  FIELD_NAME_SUBGROUP[field.name] ?? field.group_name;

const getGroupName = (field: FormField) => getFieldSubgroupName(field);

const getGroupTitle = (field: FormField) => {
  if (field.is_custom_group) {
    return field.group_name;
  }

  return i18n.t(`default_form_group.${getFieldSubgroupName(field)}`);
};

const sortFieldsByOrder = (fields: FormField[]) =>
  fields.sort((a, b) => a.order - b.order);

const sortGroupsByOrder = (groups: FieldGroup[]) =>
  groups.sort((a, b) => {
    if (a.name === FIELD_SUBGROUP.legal_name && b.name !== FIELD_SUBGROUP.legal_name) {
      return -1;
    }

    if (a.name !== FIELD_SUBGROUP.legal_name && b.name === FIELD_SUBGROUP.legal_name) {
      return 1;
    }

    return a.order - b.order;
  });

const isActive = (field: FormField) => field.is_active;
const isOnlyForLeader = (isLeaderGuest: boolean) => (field: FormField) =>
  field.is_leaders_field ? field.is_required || isLeaderGuest : true;
const isDefaultInFieldsMapper =
  (formFieldsMapper: FormFieldsMapper) => (field: FormField) =>
    field.is_custom ? true : Boolean(formFieldsMapper[field.name as GUEST_FORM_NAMES]);

const fieldsFilter =
  (formFieldsMapper: FormFieldsMapper, isLeaderGuest: boolean) => (field: FormField) => {
    return (
      isActive(field) &&
      isOnlyForLeader(isLeaderGuest)(field) &&
      isDefaultInFieldsMapper(formFieldsMapper)(field)
    );
  };

export const groupByGroupName = (
  formFields: AddFormType,
  formFieldsMapper: FormFieldsMapper,
  isLeaderGuest: boolean,
) => {
  const groups = sortFieldsByOrder(Object.values(formFields))
    .filter(fieldsFilter(formFieldsMapper, isLeaderGuest))
    .reduce((groupList, field) => {
      const groupName = getGroupName(field);
      const group = groupList.find(({name}) => name === groupName);
      if (group) {
        group.fields.push(field);
      } else {
        groupList.push({
          name: groupName,
          title: getGroupTitle(field),
          fields: [field],
          isCustom: field.is_custom_group,
          order: field.group_order,
        });
      }

      return groupList;
    }, new Array<FieldGroup>());

  return sortGroupsByOrder(groups);
};
